import { useState, type FormEvent, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { useAuth } from "../../context/AuthContext";
import { toast } from "react-toastify";
import { getErrorMessage } from "../../utils/errors";
import { Input } from "../../components/common/Input/Input";
import { Button } from "../../components/common/Button/Button";
import "./Profile.css";

type ProfileTab = "personal" | "security";

function Profile() {
  const { t, i18n } = useTranslation();
  const { user, updateProfile } = useAuth();
  const navigate = useNavigate();

  const [activeTab, setActiveTab] = useState<ProfileTab>("personal");

  // Personal Info Form State
  const [name, setName] = useState("");
  const [phone, setPhone] = useState("");
  const [personalErrors, setPersonalErrors] = useState<Record<string, string>>(
    {},
  );
  const [personalLoading, setPersonalLoading] = useState(false);

  // Security Form State
  const [currentPassword, setCurrentPassword] = useState("");
  const [newPassword, setNewPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [securityErrors, setSecurityErrors] = useState<Record<string, string>>(
    {},
  );
  const [securityLoading, setSecurityLoading] = useState(false);

  // Redirect to login if not authenticated
  useEffect(() => {
    if (!user) {
      navigate("/login");
    } else {
      setName(user.name || "");
      setPhone(user.phoneNumber || "");
    }
  }, [user, navigate]);

  if (!user) {
    return null;
  }

  const isRtl = i18n.resolvedLanguage?.startsWith("ar") ?? false;

  // Personal Info Validation
  const validatePersonal = () => {
    const errs: Record<string, string> = {};
    if (!name.trim()) {
      errs.name = t("auth.validation.nameRequired");
    } else if (name.trim().length < 2) {
      errs.name = t("auth.validation.nameMin");
    }
    return errs;
  };

  const handlePersonalSubmit = async (e: FormEvent) => {
    e.preventDefault();
    const errs = validatePersonal();
    setPersonalErrors(errs);
    if (Object.keys(errs).length > 0) return;

    setPersonalLoading(true);
    try {
      await updateProfile({
        name: name.trim(),
        phoneNumber: phone.trim(),
      });
      toast.success(t("profile.updateSuccess"));
    } catch (err) {
      const msg = await getErrorMessage(err, t("profile.updateFailed"));
      toast.error(msg);
    } finally {
      setPersonalLoading(false);
    }
  };

  // Security Validation
  const validateSecurity = () => {
    const errs: Record<string, string> = {};
    if (!currentPassword) {
      errs.currentPassword = t("profile.currentPasswordRequired");
    }
    if (!newPassword) {
      errs.newPassword = t("auth.validation.passwordRequired");
    } else if (newPassword.length < 6) {
      errs.newPassword = t("profile.passwordMin");
    }
    if (newPassword !== confirmPassword) {
      errs.confirmPassword = t("profile.passwordMismatch");
    }
    return errs;
  };

  const handleSecuritySubmit = async (e: FormEvent) => {
    e.preventDefault();
    const errs = validateSecurity();
    setSecurityErrors(errs);
    if (Object.keys(errs).length > 0) return;

    setSecurityLoading(true);
    try {
      await updateProfile({
        currentPassword,
        newPassword,
      });
      toast.success(t("profile.updateSuccess"));
      // Clear password fields
      setCurrentPassword("");
      setNewPassword("");
      setConfirmPassword("");
    } catch (err) {
      const msg = await getErrorMessage(err, t("profile.updateFailed"));
      toast.error(msg);
    } finally {
      setSecurityLoading(false);
    }
  };

  return (
    <main className={`profile-page ${isRtl ? "rtl" : "ltr"}`}>
      <div className="profile-container container">
        <header className="profile-header">
          <h1>{t("profile.title")}</h1>
          <p>{t("profile.subtitle")}</p>
        </header>

        <div className="profile-layout">
          {/* Left Column: Avatar & Basic Stats */}
          <aside className="profile-card profile-sidebar">
            <div className="profile-avatar-container">
              <div className="profile-avatar-glow"></div>
              <div className="profile-avatar-inner">
                {user.name ? user.name.charAt(0).toUpperCase() : "U"}
              </div>
            </div>
            <div className="profile-info-brief">
              <h2>{user.name}</h2>
              <p className="profile-email-badge">
                <svg
                  viewBox="0 0 24 24"
                  width="14"
                  height="14"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="2"
                >
                  <path d="M4 4h16c1.1 0 2 .9 2 2v12c0 1.1-.9 2-2 2H4c-1.1 0-2-.9-2-2V6c0-1.1.9-2 2-2z" />
                  <polyline points="22,6 12,13 2,6" />
                </svg>
                {user.email}
              </p>
            </div>

            <div className="profile-nav-tabs">
              <button
                type="button"
                className={`profile-tab-btn ${activeTab === "personal" ? "active" : ""}`}
                onClick={() => setActiveTab("personal")}
              >
                <svg
                  viewBox="0 0 24 24"
                  width="18"
                  height="18"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="2"
                >
                  <path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2" />
                  <circle cx="12" cy="7" r="4" />
                </svg>
                <span>{t("profile.personalInfo")}</span>
              </button>

              <button
                type="button"
                className={`profile-tab-btn ${activeTab === "security" ? "active" : ""}`}
                onClick={() => setActiveTab("security")}
              >
                <svg
                  viewBox="0 0 24 24"
                  width="18"
                  height="18"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="2"
                >
                  <rect x="3" y="11" width="18" height="11" rx="2" ry="2" />
                  <path d="M7 11V7a5 5 0 0 1 10 0v4" />
                </svg>
                <span>{t("profile.security")}</span>
              </button>
            </div>
          </aside>

          {/* Right Column: Dynamic Form Card */}
          <section className="profile-card profile-content">
            {activeTab === "personal" && (
              <form
                onSubmit={handlePersonalSubmit}
                noValidate
                className="profile-form"
              >
                <h3 className="section-title">{t("profile.personalInfo")}</h3>

                <div className="profile-form-grid">
                  <Input
                    id="profile-name"
                    type="text"
                    label={t("profile.name")}
                    value={name}
                    onChange={(e) => setName(e.target.value)}
                    error={personalErrors.name}
                    isTouched={Boolean(personalErrors.name)}
                    className="form-group"
                  />

                  <div className="input-group-readonly form-group">
                    <label className="input-label-readonly">
                      {t("profile.email")}
                    </label>
                    <div className="input-control-readonly">
                      <span>{user.email}</span>
                      <svg
                        viewBox="0 0 24 24"
                        width="16"
                        height="16"
                        fill="none"
                        stroke="currentColor"
                        strokeWidth="2"
                        className="lock-icon"
                      >
                        <rect
                          x="3"
                          y="11"
                          width="18"
                          height="11"
                          rx="2"
                          ry="2"
                        />
                        <path d="M7 11V7a5 5 0 0 1 10 0v4" />
                      </svg>
                    </div>
                  </div>

                  <Input
                    id="profile-phone"
                    type="tel"
                    label={t("profile.phone")}
                    placeholder={t("profile.phonePlaceholder")}
                    value={phone}
                    onChange={(e) => setPhone(e.target.value)}
                    className="form-group"
                  />
                </div>

                <div className="profile-form-actions">
                  <Button
                    type="submit"
                    variant="modern"
                    loading={personalLoading}
                    disabled={
                      name === user.name && phone === (user.phoneNumber || "")
                    }
                  >
                    {t("profile.saveChanges")}
                  </Button>
                </div>
              </form>
            )}

            {activeTab === "security" && (
              <form
                onSubmit={handleSecuritySubmit}
                noValidate
                className="profile-form"
              >
                <h3 className="section-title">{t("profile.security")}</h3>

                <div className="profile-form-grid">
                  <Input
                    id="current-password"
                    type="password"
                    label={t("profile.currentPassword")}
                    placeholder="••••••••"
                    value={currentPassword}
                    onChange={(e) => setCurrentPassword(e.target.value)}
                    error={securityErrors.currentPassword}
                    isTouched={Boolean(securityErrors.currentPassword)}
                    className="form-group"
                  />

                  <Input
                    id="new-password"
                    type="password"
                    label={t("profile.newPassword")}
                    placeholder="••••••••"
                    value={newPassword}
                    onChange={(e) => setNewPassword(e.target.value)}
                    error={securityErrors.newPassword}
                    isTouched={Boolean(securityErrors.newPassword)}
                    className="form-group"
                  />

                  <Input
                    id="confirm-password"
                    type="password"
                    label={t("profile.confirmNewPassword")}
                    placeholder="••••••••"
                    value={confirmPassword}
                    onChange={(e) => setConfirmPassword(e.target.value)}
                    error={securityErrors.confirmPassword}
                    isTouched={Boolean(securityErrors.confirmPassword)}
                    className="form-group"
                  />
                </div>

                <div className="profile-form-actions">
                  <Button
                    type="submit"
                    variant="modern"
                    loading={securityLoading}
                    disabled={
                      !currentPassword || !newPassword || !confirmPassword
                    }
                  >
                    {t("profile.saveChanges")}
                  </Button>
                </div>
              </form>
            )}
          </section>
        </div>
      </div>
    </main>
  );
}

export default Profile;
